import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/hu';
import { useMediaQuery, useTheme } from '@mui/material';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { CommentTemplate } from '../../shared/CommentTemplate';
import DatePickerController from '../../shared/DatePickerController';
import { OutlinedButton } from 'components/styled/OutlinedButton';
import { QuotationNextButton } from '../shared/QuotationNextButton';
import useGeneralDialog from '../../../hooks/dialogs/useGeneralDialog';
import useQuotation from '../../../hooks/quotations/useQuotation';
import useFullScreenLoading from '../../../hooks/useFullScreenLoading';
import type { QuotationData } from 'models/quotations/Quotations';
import type { CommentTemplateRefProps } from '../../../models/CommentTemplate';
import type { QuoteInfoForm as QuoteInfoFormType } from './types/QuoteInfoForm';
import { AddUpdateCommentTemplate } from 'components/comment-templates/AddUpdateCommentTemplateModal';
import TextFieldControlled from 'components/shared/TextFieldControlled';
import { quoteInfoFormSchema } from './schema/quoteInfoSchema';

interface QuoteInfoFormProps {
    quoteData?: QuotationData;
    handleNext: (index: number, updatedItems?: any) => void;
    index: number;
    updateProducts?: VoidFunction;
}

export const QuotationDetails: React.FC<QuoteInfoFormProps> = (props) => {
    const { quoteData, handleNext, updateProducts, index } = props;

    const fullScreenLoading = useFullScreenLoading();

    const commentRef = useRef<CommentTemplateRefProps>(null);

    const quotation = useQuotation();

    const theme = useTheme();

    const small = useMediaQuery(theme.breakpoints.down('md'));

    const dialog = useGeneralDialog();

    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.FIELDS.QUOTATION_DETAILS',
    });

    const form = useForm<QuoteInfoFormType>({
        mode: 'onChange',
        resolver: yupResolver(quoteInfoFormSchema(t)),
        defaultValues: {
            name: quoteData.name,
            valid_from: quoteData?.valid_from
                ? dayjs(quoteData?.valid_from)
                : dayjs(),
            valid_until: quoteData?.valid_until
                ? dayjs(quoteData?.valid_until)
                : dayjs().add(1, 'day'),
            comment_1: quoteData.comment_1,
        },
    });

    const handleCommentSelect = (value: string): void => {
        const current = form.getValues('comment_1');
        form.setValue('comment_1', current ? `${current} ${value}` : value, {
            shouldDirty: true,
        });
    };

    const [validFrom, validUntil] = form.watch(['valid_from', 'valid_until']);

    const handleOpenCreateNewCommentDialog = (): void => {
        dialog.showDialog({
            title: t('ADD_NEW.TITLE', {
                keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.COMMENT_TEMPLATE',
            }),
            confirmLabel: t('ADD_NEW.CONFIRM', {
                keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.COMMENT_TEMPLATE',
            }),
            closeLabel: t('ADD_NEW.CANCEL', {
                keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.COMMENT_TEMPLATE',
            }),
            preSetContent: { note: form.getValues('comment_1'), title: '' },
            component: AddUpdateCommentTemplate,
            onCloseWithSuccess: commentRef.current?.refresh,
        });
    };

    const handleSubmit: SubmitHandler<QuoteInfoFormType> = async (formData) => {
        const mappedFormData: Pick<
            QuotationData,
            'name' | 'valid_from' | 'valid_until' | 'comment_1'
        > = {
            name: formData.name,
            valid_from: formData.valid_from.toISOString(),
            valid_until: formData.valid_until.toISOString(),
            comment_1: formData.comment_1,
        };

        fullScreenLoading.setFullScreenLoading(true);

        quotation
            .saveDetails(mappedFormData, quoteData.id)
            .then((response) => {
                updateProducts();
                handleNext(index, response);
            })
            .finally(() => {
                fullScreenLoading.setFullScreenLoading(false);
            });
    };

    const saveNoteTemplateButton = useMemo(
        () => (
            <OutlinedButton
                sx={{
                    width: small ? '100%' : 'auto',
                    margin: '12px 0',
                    height: '40px',
                }}
                onClick={handleOpenCreateNewCommentDialog}
            >
                {t('ADD_NEW_COMMENT_TEMPLATE')}
            </OutlinedButton>
        ),
        [small, handleOpenCreateNewCommentDialog, t]
    );

    const gridTemplateAreas = useMemo((): string => {
        if (small) {
            return `
                "name"
                "from"
                "until"
                "note"
                "templates"
                "footer"
            `;
        }

        return `
            "name from"
            ". until"
            "note templates"
            "footer footer"
        `;
    }, [small]);

    return (
        <form
            onSubmit={form.handleSubmit(handleSubmit)}
            style={{
                paddingBlockStart: '24px',
                display: 'grid',
                gap: small ? '18px' : '12px',
                gridTemplateAreas,
            }}
        >
            <span style={{ gridArea: 'name' }}>
                <TextFieldControlled
                    control={form.control}
                    name="name"
                    label={t('NAME')}
                    sx={{
                        width: '100%',
                        '& .MuiInputBase-input': {
                            backgroundColor: 'white',
                            height: 'px',
                        },
                    }}
                />
            </span>

            <span style={{ gridArea: 'from' }}>
                <DatePickerController
                    control={form.control}
                    name="valid_from"
                    label={t('START_DATE')}
                    maxDate={validUntil}
                />
            </span>

            <span style={{ gridArea: 'until' }}>
                <DatePickerController
                    control={form.control}
                    name="valid_until"
                    label={t('END_DATE')}
                    minDate={validFrom.add(1, 'minute')}
                />
            </span>

            <span style={{ gridArea: 'templates' }}>
                <CommentTemplate
                    handleCommentSelect={handleCommentSelect}
                    ref={commentRef}
                />
            </span>

            <span style={{ gridArea: 'note' }}>
                <DemoItem label={t('COMMENT')}>
                    <TextFieldControlled
                        control={form.control}
                        name="comment_1"
                        sx={{
                            backgroundColor: 'white',
                            '& .MuiFilledInput-input': {
                                padding: '0px !important',
                            },
                            '& .MuiFilledInput-root': {
                                minHeight: '50px',

                                padding: '5px 5px 5px 5px !important',
                                '@media (max-width: 600px)': {
                                    padding: '2px 0px 2px 5px !important',
                                },
                            },
                        }}
                        multiline
                        minRows={5}
                        maxRows={8}
                        hiddenLabel
                    />
                </DemoItem>
                {small && saveNoteTemplateButton}
            </span>

            <span
                style={{
                    gridArea: 'footer',
                    display: 'flex',
                    flexDirection: small ? 'column' : 'row',
                    justifyContent: 'space-between',
                }}
            >
                {!small && saveNoteTemplateButton}

                <QuotationNextButton fullWidth={small} />
            </span>
        </form>
    );
};
